import adblockDetect from 'adblock-detect';
import { VASTTracker } from '@dailymotion/vast-client';
import PlayerBase from '../player_base';
import Logger from '../../utils/logger';
import simpleInterval from '../../utils/simple_interval';
import { requestAds } from '../../utils/request';
import {
  fetchAdFromVAST,
  getBestMedia,
  getValidAds,
  trackEvents,
} from './utils';
import { AD_ROLL_TYPES } from '../../../common/constants';
import platform, { OS_TYPES } from '../../utils/platform';

const ERROR_CODES = {
  TRAFFICKING_ERROR: '200',
  FILE_NOT_FOUND: '401',
  NO_SUPPORTED_MEDIAFILE: '403',
  PROBLEM_DISPLAYING_MEDIAFILE: '405',
};

export default class AdsPlayer extends PlayerBase {
  constructor(parent) {
    super(parent);

    this.parent.addEventListener('ontimeupdate', this._onPlayerTimeUpdate);
    this.parent.addEventListener('seeked', this._onPlayerSeeked);
    this.parent.addEventListener('chromecastAds', this._onChromecastAds);
    this.parent.addEventListener('playing', this._onPlayerPlaying);
    this.parent.addEventListener('streamEnded', this._onPlayerStreamEnded);
    this.parent.addEventListener('destroy', this._onPlayerDestroy);

    this._skippableTimer = simpleInterval(this._countSecondsLeftToSkip, 1000);
    this._intervalTimer = simpleInterval(this._countSecondsToAd, 1000);

    this._ads = [];
    this._totalAdsCount = 0;
    this._adToPlay = null;

    this._midroll = [];

    this._adTypes = {
      midroll: [],
    };
    this._secondsToAd = 0;

    this._isPrerollPlaying = false;
    this._isPostrollPlaying = false;
    this._adsPointsOffset = [];
  }

  _trackEvents(event, errorCode, ad) {
    const vastTracker = this._adToPlay?.getTracker?.();
    trackEvents(vastTracker, event, errorCode, ad);
  }

  _getAdDuration() {
    return this._adToPlay.ad?.getLinear?.().duration;
  }
  _getAdTitle() {
    return this._adToPlay.ad?.title;
  }

  _onPlayerPlaying = () => {
    this._prepareNextInterval(true);
  };

  _onPlayerDestroy = () => {
    Logger.debug('_onPlayerDestroy', this.model);
    this.unload();
  };

  _onDeactivated() {
    this.model = this.model.merge({
      adsPlaying: false,
    });

    const {
      _isPrerollPlaying,
      _isPostrollPlaying,
    } = this;

    this._isPrerollPlaying = false;
    this._isPostrollPlaying = false;

    this.parent.dispatchEvent('adsDeactivated', {
      afterPreroll: _isPrerollPlaying,
      afterPostroll: _isPostrollPlaying,
    });
  }

  /**
   * Undergoes preparations for playing interval rolls. Note that interval rolls
   * will only be played for live streams
   * @param {boolean} shouldPlayInitial - Whether the initial interval should be
   * prepared, or a normal interval
   */
  _prepareNextInterval(shouldPlayInitial = false) {
    if (this._intervalTimer.isStarted()) {
      return;
    }

    if (this.model.isLive && this._adTypes.intervalroll) {
      this._secondsToAd = shouldPlayInitial
        ? this._adTypes.intervalroll.initial
        : this._adTypes.intervalroll.interval;
      Logger.debug('Starting interval roll timer now!', new Date());
      this._intervalTimer.start();
    }
  }

  async loadAdsForAsset(assetId, adParameters) {
    const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);
    const res = isMobile ? {} : await requestAds(assetId, adParameters) || {};

    this._adTypes.preroll = res.preroll;
    this._adTypes.postroll = res.postroll;
    this._adTypes.intervalroll = res.intervalroll;

    // prepare midrolls
    // filter out midrolls with offset < playbackStartTime
    // so that we don't play unrelevent midrolls if user continue watching
    this._adTypes.midroll = res.midroll?.offsets
      .sort((o1, o2) => o1 - o2) // sort by offset asc
      .map(offset => ({
        offset,
        url: res.midroll.url,
      })) || [];

    this._midroll = [...this._adTypes.midroll];
    this._adTypes.midroll = this._adTypes.midroll
      .filter(midroll => midroll.offset >= this.model.playbackStartTime) || [];

    Logger.debug(`Got ads for the asset ${assetId}:`);
    Logger.debug(`Preroll: ${!!this._adTypes.preroll}`);
    Logger.debug(`Midrolls: ${this._adTypes.midroll.length}`);
    Logger.debug(`Postroll: ${!!this._adTypes.postroll}`);
    Logger.debug(`Intervalrolls: ${!!this._adTypes.intervalroll}`);

    this.parent.dispatchEvent('adsAsset', this._adTypes);

    this._adsPointsOffset = [
      ...this._adTypes.preroll ? [0] : [],
      ...this._midroll.map(m => m.offset),
      // set offset to -1 if there is postroll as we don't have video duration at this time yet
      ...this._adTypes.postroll ? [-1] : [],
    ];
  }

  isPrerollAvailable() {
    return !!this._adTypes.preroll && !this.model.isAirPlaying;
  }

  async _playAds(url) {
    if (this.model.isAirPlaying) return;

    try {
      const rawAds = await fetchAdFromVAST(url);
      const ads = getValidAds(rawAds, (invalidAd) => {
        // Couldn't find any media to play in the ad, track a trafficking error
        this._trackEvents('error', ERROR_CODES.TRAFFICKING_ERROR, invalidAd);
      });

      if (!ads.length) {
        throw new Error(`url ${url} contains no playable ads`);
      }
      this._ads = ads;
      this._totalAdsCount = ads.length;

      this.parent.dispatchEvent('adsActivated');
      this.model = this.model.merge({
        adsPlaying: true,
      });

      this.parent._shakaPlayer.pause();
      this.parent.currentPlayer = this.parent.adsPlayer;

      this._playAd(ads[0]);
    } catch (e) {
      this.clear();
      throw e;
    }
  }

  unload() {
    Logger.debug('Unload');

    this._adToPlay = null;

    this._midroll = [];
    this._adTypes = {
      midroll: [],
    };
    this._secondsToAd = 0;
    this._ads = [];
    this._totalAdsCount = 0;

    this._isPrerollPlaying = false;
    this._isPostrollPlaying = false;

    this._adsPointsOffset = [];

    this.clear();
  }

  clear() {
    Logger.debug('Clear');
    this._onDeactivated();
    this.parent.currentPlayer = this.parent._shakaPlayer;

    this._unInstallListeners();

    if (this._skippableTimer.isStarted()) {
      this._skippableTimer.stop();
    }
    if (this._intervalTimer.isStarted()) {
      this._intervalTimer.stop();
    }

    if (this.parent.adsPlayerElement?.src) {
      this.parent.adsPlayerElement.pause();
      this.parent.adsPlayerElement.src = '';
    }
  }

  toggleMute() {
    if (this.model.volume === 0) {
      this.changeVolume(this.parent._getActiveVideoVolume());
    } else {
      this.changeVolume(0);
    }
  }

  changeVolume(volume) {
    const vastTracker = this._adToPlay?.getTracker?.();
    if (this.model.volume === 0 && volume > 0) {
      vastTracker?.setMuted(false);
    } else if (this.model.volume > 0 && volume === 0) {
      vastTracker.setMuted(true);
    }

    this.parent.adsPlayerElement.volume = volume;
    this.parent._saveVolume(volume);
  }

  async play() {
    await this.parent.adsPlayerElement.play()
      .catch((e) => {
        Logger.warn('Failed to play ad:', e);
      });
    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker?.setPaused(false);
  }

  pause() {
    this.parent.adsPlayerElement.pause();
    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker?.setPaused(true);
  }

  async togglePlay() {
    if (this.model.isPlaying) {
      this.pause();
    } else {
      await this.play();
    }
  }

  fastForward() {
    Logger.debug('No fastForward is available, ignoring');
  }

  rewind() {
    Logger.debug('No rewind is available, ignoring');
  }

  seek() {
    Logger.debug('No seeking is available, ignoring');
  }

  skip() {
    if (this._adToPlay.secondsLeftToSkip === 0) {
      this._trackEvents('skip');
      this._trackEvents('closeLinear');
      this._trackEvents('close');
      this._onEnded();
    }
  }

  openAdWebsite() {
    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker.click(null);
  }

  _getAdsCurrentTime() {
    return Math.round(this.parent.adsPlayerElement.currentTime);
  }

  _countSecondsLeftToSkip = () => {
    if (!this.model.isPlaying) { // do not advance skip countdown while ads are paused
      return;
    }

    if (this._adToPlay.secondsLeftToSkip > 0) {
      this._adToPlay.secondsLeftToSkip = Math.max(this._adToPlay.secondsLeftToSkip - 1, 0);

      if (this._adToPlay.secondsLeftToSkip === 0) {
        this._skippableTimer.stop();
      }
    }
  };

  _countSecondsToAd = () => {
    Logger.debug('_countSecondsToAd', this._secondsToAd);
    if (this._secondsToAd <= 0 && this.model.isPlaying) {
      this._playIntervalRoll();
      return;
    }

    if (!this.model.isBuffering && this.model.isPlaying) {
      this._secondsToAd -= 1;
    }
  };

  _playAd(ad) {
    this._ads = this._ads.filter(item => item !== ad);
    Logger.debug(`Playing ad "${ad.title}", ${this._ads.length} ads to go`);

    const linear = ad?.getLinear?.();
    const vastTracker = new VASTTracker(null, ad, linear);

    if (linear?.videoClickThroughURLTemplate?.url) {
      vastTracker.on('clickthrough', (url) => {
        window.open(url, '_blank');
      });
    }

    this._adToPlay = {
      ad,
      secondsLeftToSkip: -1,
      adHasLink: !!linear?.videoClickThroughURLTemplate?.url,
      getTracker: () => vastTracker,
    };

    const {
      offsetWidth,
      offsetHeight,
    } = this.parent.adsPlayerElement.parentNode;

    const media = getBestMedia(ad?.getLinear?.(), {
      width: offsetWidth,
      height: offsetHeight,
    });

    if (!media) {
      this._trackEvents('error', ERROR_CODES.NO_SUPPORTED_MEDIAFILE);
      Logger.warn(`Failed to play ad "${ad.adTitle}": no matching media found`);

      if (this._ads.length) {
        this._playAd(this._ads[0]);
      } else {
        this.clear();
      }
      return;
    }

    this._adToPlay = {
      ...this._adToPlay,
      src: media.fileURL,
    };

    // handle skippable ads
    const skipoffset = ad?.getLinear?.().skipDelay;
    if (skipoffset) {
      this._adToPlay = {
        ...this._adToPlay,
        secondsLeftToSkip: Math.round(skipoffset),
      };
      Logger.debug(`Ad "${ad.adTitle}" could be skipped in ${this._adToPlay.secondsLeftToSkip} seconds`);
    }

    this._installListeners();

    this.parent.adsPlayerElement.src = this._adToPlay.src;
    this.parent.adsPlayerElement.volume = this.model.volume;
    this.parent.adsPlayerElement.play()
      .catch((e) => {
        Logger.warn('Failed to play ad:', e);
      });
  }

  _installListeners() {
    this.parent.adsPlayerElement.addEventListener('playing', this._onPlaying);
    this.parent.adsPlayerElement.addEventListener('timeupdate', this._onTimeUpdate);
    this.parent.adsPlayerElement.addEventListener('play', this._onPlay);
    this.parent.adsPlayerElement.addEventListener('pause', this._onPause);
    this.parent.adsPlayerElement.addEventListener('ended', this._onEnded);
    this.parent.adsPlayerElement.addEventListener('volumechange', this._onVolumeChange);
    this.parent.adsPlayerElement.addEventListener('stalled', this._onStalled);
    this.parent.adsPlayerElement.addEventListener('error', this._onError);
  }

  _unInstallListeners() {
    this.parent.adsPlayerElement.removeEventListener('playing', this._onPlaying);
    this.parent.adsPlayerElement.addEventListener('timeupdate', this._onTimeUpdate);
    this.parent.adsPlayerElement.removeEventListener('play', this._onPlay);
    this.parent.adsPlayerElement.removeEventListener('pause', this._onPause);
    this.parent.adsPlayerElement.removeEventListener('ended', this._onEnded);
    this.parent.adsPlayerElement.removeEventListener('volumechange', this._onVolumeChange);
    this.parent.adsPlayerElement.removeEventListener('stalled', this._onStalled);
    this.parent.adsPlayerElement.removeEventListener('error', this._onError);
  }

  async _playIntervalRoll() {
    Logger.debug('Stopping interval roll timer now!', new Date());
    this._intervalTimer.stop();
    // Sanity check against the ads info
    if (!this._adTypes.intervalroll || !this._adTypes.intervalroll.url) {
      Logger.warn('Interval rolls have not been defined. Skipping!');
    } else {
      try {
        await this._playAds(this._adTypes.intervalroll.url);
      } catch (e) {
        Logger.warn('Failed to play intervalroll', e);
      }
    }
  }

  async _playMidroll(midroll) {
    try {
      this._adTypes.midroll = this._adTypes.midroll.filter(item => item !== midroll);

      await this._playAds(midroll.url);
    } catch (error) {
      Logger.warn('Failed to play midroll', error);
    }
  }

  async playPreroll() {
    try {
      this.parent.dispatchEvent(AD_ROLL_TYPES.preroll);
      this._isPrerollPlaying = true;

      await this._playAds(this._adTypes.preroll);
    } catch (error) {
      Logger.warn('Failed to play preroll', error);
    }
  }

  _onPlaying = () => {
    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker.trackImpression();

    this.model = this.model.merge({
      isPlaying: true,
      adsPlaying: true,
    });

    if (this._adToPlay.secondsLeftToSkip > 0 && !this._skippableTimer.isStarted()) {
      this._skippableTimer.start();
    }
    this.parent.dispatchEvent('adsPlaying', {
      playerInfo: {
        currentTime: this.parent.adsPlayerElement.currentTime,
        seekRange: {
          start: 0,
          startAbsolute: 0,
          end: this._getAdDuration(),
          endAbsolute: this._getAdDuration(),
        },
      },
      adsCount: this._totalAdsCount,
      adsLeft: this._ads.length,
      adTitle: this._getAdTitle(),
      adHasLink: this._adToPlay.adHasLink,
    });
  };

  _onPlay = () => {
    this.model = this.model.merge({
      isPlaying: true,
    });
    this.parent.dispatchEvent('adsPlay');

    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker?.setPaused(false);
  };
  _onPause = () => {
    this.model = this.model.merge({
      isPlaying: false,
    });
    this.parent.dispatchEvent('adsPause');

    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker?.setPaused(true);
  };

  _onStalled = () => {
    this._trackEvents('error', ERROR_CODES.FILE_NOT_FOUND);
    this.clear();
  };

  _onError = (e) => {
    if (!this._adToPlay) {
      return;
    }
    Logger.warn('Ads player error:', e);
    this._trackEvents('error', ERROR_CODES.PROBLEM_DISPLAYING_MEDIAFILE);
    this.clear();

    this.parent._errorhandlingService?.onAdsError(e);
  };

  _onPlayerTimeUpdate = (e) => {
    if (this.model.isLive) {
      return;
    }

    // Playing VOD or Catchup. Show midrolls. Round currentTime to nearest 0.5
    const currentTime = Math.round(e.detail.playerInfo.currentTime * 2) / 2;
    if (!this._adTypes.midroll.length) {
      return;
    }

    const midrollToPlay = this._adTypes.midroll.find(item => item.offset === currentTime);
    if (midrollToPlay) {
      this._playMidroll(midrollToPlay);
    }
  };

  _onChromecastAds = (e) => {
    if (this.model.isLive || !e.detail.breakId) {
      return;
    }

    const [rollName, time] = e.detail.breakId.split('-');

    if (rollName === AD_ROLL_TYPES.preroll) {
      delete this._adTypes.preroll;
    }

    if (rollName === AD_ROLL_TYPES.midroll) {
      this._adTypes.midroll = this._adTypes.midroll.filter(item => item.offset === time);
    }

    if (rollName === AD_ROLL_TYPES.postroll) {
      delete this._adTypes.postroll;
    }
  };

  async _playPostroll() {
    try {
      const url = this._adTypes.postroll;

      this._adTypes.postroll = undefined;
      this._isPostrollPlaying = true;

      await this._playAds(url);
    } catch (error) {
      Logger.warn('Failed to play postroll', error);
    }
  }

  _onPlayerStreamEnded = () => {
    if (this._adTypes.postroll && !this.model.isAirPlaying) {
      // skip midrolls that are configured after video has ended
      this._adTypes.midroll = this._adTypes.midroll.filter(item => item.offset < this.model.duration);
      this._midroll = this._midroll.filter(item => item.offset < this.model.duration);
      // Check if there are any midrolls left to play to preserve the order of ads playing
      if (this._adTypes.midroll.length) {
        this._playMidroll(this._adTypes.midroll[0]);
      } else {
        this._playPostroll();
      }
    }
  };

  _onPlayerSeeked = () => {
    const currentTime = Math.round(this.model.currentTime * 2) / 2;

    // handle seek backwards and re-populate midroll with upcoming midrolls
    const newMidroll = this._midroll.filter(item => item.offset >= currentTime);
    if (newMidroll.length > this._adTypes.midroll.length) {
      this._adTypes.midroll = newMidroll;
    }

    if (this.model.isLive || !this._adTypes.midroll.length) {
      return;
    }

    const midrollToPlay = this._adTypes.midroll.find(item => item.offset < currentTime);
    if (!midrollToPlay) {
      return;
    }

    if (currentTime !== this.model.duration || !this._adTypes.postroll) {
      this._playMidroll(midrollToPlay);
    }
  };

  _onVolumeChange = () => {
    this.parent.dispatchEvent('volumechange', {});
  };

  _onTimeUpdate = () => {
    // sometimes we receive timeupdate even after pause() call
    if (!this.model.adsPlaying) {
      return;
    }

    const adsCurrentTimeFormatted = this._getAdsCurrentTime();

    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker?.setProgress(this.parent.adsPlayerElement.currentTime);

    this.parent.dispatchEvent('ontimeupdateads', {
      playerInfo: {
        currentTime: adsCurrentTimeFormatted,
        adsDuration: this._getAdDuration(),
        secondsLeftToSkip: this._adToPlay.secondsLeftToSkip,
      },
    });
  };

  _onEnded = () => {
    Logger.debug('adsEnded');

    const vastTracker = this._adToPlay?.getTracker?.();
    vastTracker?.complete();

    if (this._ads.length > 0) {
      this._playAd(this._ads[0]);
      return;
    }

    const midrollToPlay = this._adTypes.midroll.find(item => item.offset < this.model.currentTime);
    if (midrollToPlay) {
      this._playMidroll(midrollToPlay);
      return;
    }

    if (this._adTypes.postroll && this.model.currentTime === this.model.duration) {
      this._playPostroll();
      return;
    }
    this.parent.dispatchEvent('adsEnded');

    this.clear();
    this._prepareNextInterval();
  };

  async assertNoAdblock() {
    if (this.model.adBlockOn === undefined) {
      this.model = this.model.merge({
        adBlockOn: await new Promise(resolve => adblockDetect(resolve)),
      });
    }

    if (this.model.adBlockOn) {
      const customAdblockError = new Error();
      customAdblockError.code = 666;
      customAdblockError.stack = 'Adblock detected';
      throw customAdblockError;
    }
  }

  destroy() {
    this.unload();

    this.parent.removeEventListener('ontimeupdate', this._onPlayerTimeUpdate);
    this.parent.removeEventListener('seeked', this._onPlayerSeeked);
    this.parent.removeEventListener('chromecastAds', this._onChromecastAds);
    this.parent.removeEventListener('playing', this._onPlayerPlaying);
    this.parent.removeEventListener('streamEnded', this._onPlayerStreamEnded);
    this.parent.removeEventListener('destroy', this._onPlayerDestroy);
  }
}
